import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PlusCircle, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { EnumSelect } from '@/components/ui/enum-select'
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field'
import { apiClient } from '@/lib/api/client'
import { FormWizard, ReviewRow, ReviewSection } from '@/src/components/wizard/FormWizard'
import {
  TextAreaField,
  TextField,
  formatLabel,
  isEmail,
  isLink,
} from '@/src/components/wizard/fields'
import { useDraftState, useFormDraft } from '@/src/components/wizard/useFormDraft'
import {
  useFormWizard,
  type FieldErrors,
  type WizardStep,
} from '@/src/components/wizard/useFormWizard'
import {
  customerEditErrorsFromApi,
  validateCustomerEditValues,
} from '@/app/(dashboard)/customers/[id]/_components/customer-edit-errors'
import type {
  CustomerAddress,
  CustomerAddressType,
  CustomerContact,
  CustomerPortalStatus,
  CustomerType,
  HelmCommunicationChannel,
} from '@/lib/api/types'

type ContactDraft = Omit<CustomerContact, 'id' | 'customer_id' | 'created_at' | 'updated_at'>
type AddressDraft = Omit<CustomerAddress, 'id' | 'customer_id' | 'created_at' | 'updated_at'>

const addressTypes: CustomerAddressType[] = [
  'BILLING',
  'SHIPPING',
  'PICKUP',
  'DELIVERY',
  'CONSIGNEE',
  'NOTIFY_PARTY',
  'OTHER',
]
const portalStatuses: CustomerPortalStatus[] = ['DISABLED', 'INVITED', 'ACTIVE', 'SUSPENDED']
const channels: HelmCommunicationChannel[] = ['EMAIL', 'WHATSAPP', 'BOTH', 'NONE']
const options = (values: readonly string[]) =>
  values.map((value) => ({ value, label: formatLabel(value) }))

type StepId = 'profile' | 'contacts' | 'addresses' | 'review'

const steps: WizardStep<StepId>[] = [
  {
    id: 'profile',
    title: 'Profile',
    description: 'Who the customer is, and how Xtellar invoices and reaches them.',
  },
  {
    id: 'contacts',
    title: 'Contacts',
    description: 'The people to deal with: finance, operations, shipping, management.',
    optional: true,
  },
  {
    id: 'addresses',
    title: 'Addresses',
    description: 'Billing, consignee, notify party, pickup and delivery addresses.',
    optional: true,
  },
  {
    id: 'review',
    title: 'Online & review',
    description: 'Portal access and where the customer is online, then a last check.',
  },
]

/** Which step owns a field the API rejected. */
const PROFILE_FIELDS = new Set([
  'name',
  'company_name',
  'tin_number',
  'email',
  'phone',
  'type',
  'contact_person',
  'whatsapp_group_id',
])

function splitList(value: string) {
  return Array.from(
    new Set(
      value
        .split(/[,\n]/)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  )
}

function blankContact(): ContactDraft {
  return {
    name: '',
    title: '',
    department: '',
    primary_email: '',
    emails: [],
    primary_phone: '',
    phones: [],
    preferred_channel: 'EMAIL',
    is_primary: false,
    portal_access: false,
    notes: '',
  }
}

function blankAddress(type: CustomerAddressType = 'SHIPPING'): AddressDraft {
  return {
    type,
    label: formatLabel(type),
    recipient_name: '',
    company_name: '',
    attention_to: '',
    phone: '',
    email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    region: '',
    postal_code: '',
    country: 'Uganda',
    raw_address: '',
    is_default_billing: type === 'BILLING',
    is_default_shipping: type === 'SHIPPING',
    notes: '',
    metadata: null,
  }
}

function addressIsEmpty(address: AddressDraft) {
  return ![
    address.recipient_name,
    address.company_name,
    address.attention_to,
    address.phone,
    address.email,
    address.city,
    address.raw_address,
  ].some((value) => value?.trim())
}

export function NewCustomerForm() {
  const navigate = useNavigate()
  const draft = useFormDraft('new-customer')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [type, setType] = useDraftState<CustomerType>(draft, 'type', 'BUSINESS')
  const [name, setName] = useDraftState(draft, 'name', '')
  const [companyName, setCompanyName] = useDraftState(draft, 'companyName', '')
  const [tinNumber, setTinNumber] = useDraftState(draft, 'tinNumber', '')
  const [email, setEmail] = useDraftState(draft, 'email', '')
  const [phone, setPhone] = useDraftState(draft, 'phone', '')
  const [currency, setCurrency] = useDraftState(draft, 'currency', 'USD')
  const [language, setLanguage] = useDraftState(draft, 'language', 'English')
  const [portalStatus, setPortalStatus] = useDraftState<CustomerPortalStatus>(
    draft,
    'portalStatus',
    'DISABLED',
  )
  const [websiteUrl, setWebsiteUrl] = useDraftState(draft, 'websiteUrl', '')
  const [socialX, setSocialX] = useDraftState(draft, 'socialX', '')
  const [socialLinkedin, setSocialLinkedin] = useDraftState(draft, 'socialLinkedin', '')
  const [socialInstagram, setSocialInstagram] = useDraftState(draft, 'socialInstagram', '')
  const [whatsappGroupId, setWhatsappGroupId] = useDraftState(draft, 'whatsappGroupId', '')
  const [notes, setNotes] = useDraftState(draft, 'notes', '')
  const [contacts, setContacts] = useDraftState<ContactDraft[]>(draft, 'contacts', [
    { ...blankContact(), is_primary: true },
  ])
  const [addresses, setAddresses] = useDraftState<AddressDraft[]>(draft, 'addresses', [
    blankAddress('BILLING'),
    blankAddress('SHIPPING'),
  ])

  function validate(id: StepId): FieldErrors {
    const errors: FieldErrors = {}
    if (id === 'profile') {
      const found = validateCustomerEditValues({
        name,
        type,
        companyName,
        contactPerson: '',
        tinNumber,
        phone,
        email,
        whatsappGroupId,
      })
      for (const [field, value] of Object.entries(found)) {
        if (value) errors[field] = value.message
      }
    }
    if (id === 'contacts') {
      contacts.forEach((contact, index) => {
        const filled = Boolean(
          contact.title?.trim() || contact.primary_email?.trim() || contact.primary_phone?.trim(),
        )
        if (filled && !contact.name.trim()) {
          errors[`contact-${index}-name`] = 'Add the name, or remove this contact.'
        }
        if (contact.primary_email?.trim() && !isEmail(contact.primary_email)) {
          errors[`contact-${index}-email`] = 'Enter a valid email address.'
        }
      })
    }
    if (id === 'addresses') {
      addresses.forEach((address, index) => {
        if (address.email?.trim() && !isEmail(address.email)) {
          errors[`address-${index}-email`] = 'Enter a valid email address.'
        }
      })
    }
    if (id === 'review') {
      if (websiteUrl.trim() && !isLink(websiteUrl)) {
        errors.website_url = 'Enter the full link, starting with https://'
      }
    }
    return errors
  }

  const wizard = useFormWizard(steps, validate)
  const errors = wizard.errors

  function updateContact(index: number, patch: Partial<ContactDraft>) {
    setContacts((current) =>
      current.map((contact, contactIndex) =>
        contactIndex === index ? { ...contact, ...patch } : contact,
      ),
    )
  }

  function setPrimaryContact(index: number) {
    setContacts((current) =>
      current.map((contact, contactIndex) => ({
        ...contact,
        is_primary: contactIndex === index,
      })),
    )
  }

  function removeContact(index: number) {
    setContacts((current) => {
      if (current.length === 1) return current
      const next = current.filter((_contact, contactIndex) => contactIndex !== index)
      return next.some((contact) => contact.is_primary)
        ? next
        : next.map((contact, contactIndex) => ({ ...contact, is_primary: contactIndex === 0 }))
    })
  }

  function updateAddress(index: number, patch: Partial<AddressDraft>) {
    setAddresses((current) =>
      current.map((address, addressIndex) =>
        addressIndex === index ? { ...address, ...patch } : address,
      ),
    )
  }

  function removeAddress(index: number) {
    setAddresses((current) =>
      current.length === 1
        ? current
        : current.filter((_address, addressIndex) => addressIndex !== index),
    )
  }

  const namedContacts = contacts.filter((contact) => contact.name.trim())
  const filledAddresses = addresses.filter((address) => !addressIsEmpty(address))

  async function handleSubmit() {
    setIsSubmitting(true)
    setError(null)

    const normalizedContactDrafts = namedContacts.map((contact) => {
      const emailList = Array.from(
        new Set(
          [contact.primary_email, ...contact.emails]
            .map((item) => item?.trim())
            .filter(Boolean) as string[],
        ),
      )
      const phoneList = Array.from(
        new Set(
          [contact.primary_phone, ...contact.phones]
            .map((item) => item?.trim())
            .filter(Boolean) as string[],
        ),
      )
      return {
        ...contact,
        name: contact.name.trim(),
        title: contact.title?.trim() || null,
        department: contact.department?.trim() || null,
        primary_email: contact.primary_email?.trim() || emailList[0] || null,
        emails: emailList,
        primary_phone: contact.primary_phone?.trim() || phoneList[0] || null,
        phones: phoneList,
        notes: contact.notes?.trim() || null,
        is_primary: contact.is_primary,
      }
    })
    const normalizedContacts = normalizedContactDrafts.some((contact) => contact.is_primary)
      ? normalizedContactDrafts
      : normalizedContactDrafts.map((contact, index) => ({ ...contact, is_primary: index === 0 }))

    const normalizedAddresses = addresses
      .map((address) => ({
        ...address,
        label: address.label.trim(),
        recipient_name: address.recipient_name?.trim() || null,
        company_name: address.company_name?.trim() || null,
        attention_to: address.attention_to?.trim() || null,
        phone: address.phone?.trim() || null,
        email: address.email?.trim() || null,
        address_line1: address.address_line1?.trim() || null,
        address_line2: address.address_line2?.trim() || null,
        city: address.city?.trim() || null,
        region: address.region?.trim() || null,
        postal_code: address.postal_code?.trim() || null,
        country: address.country?.trim() || null,
        raw_address: address.raw_address?.trim() || null,
        notes: address.notes?.trim() || null,
      }))
      .filter((address) => address.label)

    try {
      const customer = await apiClient.createCustomer({
        name: name.trim(),
        type,
        company_name: type === 'BUSINESS' ? companyName.trim() : null,
        contact_person: normalizedContacts[0]?.name || null,
        tin_number: tinNumber.trim(),
        phone: phone.trim(),
        email: email.trim(),
        whatsapp_group_id: whatsappGroupId.trim() || null,
        default_currency: currency.trim().toUpperCase() || 'USD',
        portal_status: portalStatus,
        customer_language: language.trim() || 'English',
        website_url: websiteUrl.trim() || null,
        social_profiles: {
          x: socialX.trim() || null,
          linkedin: socialLinkedin.trim() || null,
          instagram: socialInstagram.trim() || null,
        },
        notes: notes.trim() || null,
        contacts: normalizedContacts,
        addresses: normalizedAddresses,
      })

      draft.clear()
      navigate(`/customers/${customer.id}`)
    } catch (cause) {
      const mapped = customerEditErrorsFromApi(cause)
      const profileErrors: FieldErrors = {}
      for (const [field, value] of Object.entries(mapped.fields)) {
        if (value && PROFILE_FIELDS.has(field)) profileErrors[field] = value.message
      }
      if (Object.keys(profileErrors).length) {
        wizard.showServerErrors('profile', profileErrors)
      } else {
        setError(mapped.form ?? 'Failed to create customer')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const disabled = isSubmitting

  return (
    <FormWizard
      wizard={wizard}
      submitLabel="Create customer"
      busy={isSubmitting}
      busyLabel="Creating customer"
      error={error}
      errorTitle="The customer was not created"
      onSubmit={handleSubmit}
      draft={draft}
      draftLabel="customer answers"
    >
      {wizard.step.id === 'profile' && (
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="type">Customer type</FieldLabel>
            <EnumSelect
              value={type}
              onValueChange={(value) => setType(value as CustomerType)}
              options={[
                { value: 'BUSINESS', label: 'Business' },
                { value: 'INDIVIDUAL', label: 'Individual' },
              ]}
              disabled={disabled}
              triggerProps={{ id: 'type', className: 'sm:max-w-xs' }}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            {type === 'BUSINESS' && (
              <TextField
                id="company_name"
                label="Company name"
                required
                value={companyName}
                onChange={setCompanyName}
                error={errors.company_name}
                disabled={disabled}
                autoComplete="organization"
              />
            )}
            <TextField
              id="name"
              label={type === 'BUSINESS' ? 'Main contact' : 'Full name'}
              required
              value={name}
              onChange={setName}
              error={errors.name}
              disabled={disabled}
              autoComplete="name"
            />
            <TextField
              id="tin_number"
              label="TIN"
              required
              value={tinNumber}
              onChange={setTinNumber}
              error={errors.tin_number}
              disabled={disabled}
              placeholder="10 digits"
              inputMode="numeric"
            />
            <TextField
              id="email"
              label="Email"
              type="email"
              required
              value={email}
              onChange={setEmail}
              error={errors.email}
              disabled={disabled}
              placeholder="name@company.com"
              inputMode="email"
              autoComplete="email"
            />
            <TextField
              id="phone"
              label="Phone"
              type="tel"
              required
              value={phone}
              onChange={setPhone}
              error={errors.phone}
              disabled={disabled}
              placeholder="+256 7XX XXX XXX"
              autoComplete="tel"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2 text-sm">
              <p className="font-medium">Customer ID</p>
              <p className="text-muted-foreground">
                Helm assigns a permanent XTLCID number when you save. Existing customer numbers stay
                unchanged.
              </p>
            </div>
            <TextField
              id="default_currency"
              label="Invoice currency"
              value={currency}
              onChange={(value) => setCurrency(value.toUpperCase())}
              disabled={disabled}
              maxLength={3}
            />
            <TextField
              id="customer_language"
              label="Language"
              value={language}
              onChange={setLanguage}
              disabled={disabled}
            />
          </div>
        </FieldGroup>
      )}

      {wizard.step.id === 'contacts' && (
        <FieldGroup>
          {contacts.map((contact, index) => (
            <FieldSet key={index} className="rounded-md border p-4">
              <div className="flex items-center justify-between gap-3">
                <FieldLegend variant="label" className="mb-0">
                  Contact {index + 1}
                </FieldLegend>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => removeContact(index)}
                  disabled={disabled || contacts.length === 1}
                  aria-label={`Remove contact ${index + 1}`}
                >
                  <Trash2 />
                </Button>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <TextField
                  id={`contact-${index}-name`}
                  label="Name"
                  value={contact.name}
                  onChange={(value) => updateContact(index, { name: value })}
                  error={errors[`contact-${index}-name`]}
                  disabled={disabled}
                  placeholder="Chris Kyerere"
                />
                <TextField
                  id={`contact-${index}-title`}
                  label="Role"
                  value={contact.title ?? ''}
                  onChange={(value) => updateContact(index, { title: value })}
                  disabled={disabled}
                  placeholder="COO"
                />
                <TextField
                  id={`contact-${index}-department`}
                  label="Department"
                  value={contact.department ?? ''}
                  onChange={(value) => updateContact(index, { department: value })}
                  disabled={disabled}
                  placeholder="Operations"
                />
                <TextField
                  id={`contact-${index}-email`}
                  label="Email"
                  type="email"
                  inputMode="email"
                  value={contact.primary_email ?? ''}
                  onChange={(value) =>
                    updateContact(index, { primary_email: value, emails: splitList(value) })
                  }
                  error={errors[`contact-${index}-email`]}
                  disabled={disabled}
                />
                <TextField
                  id={`contact-${index}-emails`}
                  label="Other emails"
                  value={contact.emails.join(', ')}
                  onChange={(value) => updateContact(index, { emails: splitList(value) })}
                  disabled={disabled}
                  placeholder="Comma separated"
                />
                <Field>
                  <FieldLabel htmlFor={`contact-${index}-channel`}>Reach them by</FieldLabel>
                  <EnumSelect
                    value={contact.preferred_channel}
                    onValueChange={(value) =>
                      updateContact(index, {
                        preferred_channel: value as HelmCommunicationChannel,
                      })
                    }
                    options={options(channels)}
                    disabled={disabled}
                    triggerProps={{ id: `contact-${index}-channel` }}
                  />
                </Field>
                <TextField
                  id={`contact-${index}-phone`}
                  label="Phone"
                  type="tel"
                  value={contact.primary_phone ?? ''}
                  onChange={(value) =>
                    updateContact(index, { primary_phone: value, phones: splitList(value) })
                  }
                  disabled={disabled}
                />
                <TextField
                  id={`contact-${index}-phones`}
                  label="Other phones"
                  value={contact.phones.join(', ')}
                  onChange={(value) => updateContact(index, { phones: splitList(value) })}
                  disabled={disabled}
                  placeholder="Comma separated"
                />
                <div className="flex flex-wrap items-center gap-4 md:pt-6">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={contact.is_primary}
                      onCheckedChange={() => setPrimaryContact(index)}
                      disabled={disabled}
                    />
                    Main contact
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={contact.portal_access}
                      onCheckedChange={(checked) =>
                        updateContact(index, { portal_access: checked === true })
                      }
                      disabled={disabled}
                    />
                    Portal access
                  </label>
                </div>
                <TextField
                  id={`contact-${index}-notes`}
                  label="Notes"
                  className="md:col-span-3"
                  value={contact.notes ?? ''}
                  onChange={(value) => updateContact(index, { notes: value })}
                  disabled={disabled}
                />
              </div>
            </FieldSet>
          ))}
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setContacts((current) => [...current, blankContact()])}
              disabled={disabled}
            >
              <PlusCircle />
              Add contact
            </Button>
          </div>
        </FieldGroup>
      )}

      {wizard.step.id === 'addresses' && (
        <FieldGroup>
          {addresses.map((address, index) => (
            <FieldSet key={index} className="rounded-md border p-4">
              <div className="flex items-center justify-between gap-3">
                <FieldLegend variant="label" className="mb-0">
                  {address.label || `Address ${index + 1}`}
                </FieldLegend>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => removeAddress(index)}
                  disabled={disabled || addresses.length === 1}
                  aria-label={`Remove address ${index + 1}`}
                >
                  <Trash2 />
                </Button>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <Field>
                  <FieldLabel htmlFor={`address-${index}-type`}>Type</FieldLabel>
                  <EnumSelect
                    value={address.type}
                    onValueChange={(value) =>
                      updateAddress(index, {
                        type: value as CustomerAddressType,
                        label:
                          !address.label || address.label === formatLabel(address.type)
                            ? formatLabel(value)
                            : address.label,
                      })
                    }
                    options={options(addressTypes)}
                    disabled={disabled}
                    triggerProps={{ id: `address-${index}-type` }}
                  />
                </Field>
                <TextField
                  id={`address-${index}-label`}
                  label="Label"
                  value={address.label}
                  onChange={(value) => updateAddress(index, { label: value })}
                  disabled={disabled}
                />
                <TextField
                  id={`address-${index}-recipient`}
                  label="Recipient"
                  value={address.recipient_name ?? ''}
                  onChange={(value) => updateAddress(index, { recipient_name: value })}
                  disabled={disabled}
                />
                <TextAreaField
                  id={`address-${index}-address`}
                  label="Address"
                  className="md:col-span-3"
                  rows={3}
                  value={address.raw_address ?? ''}
                  onChange={(value) => updateAddress(index, { raw_address: value })}
                  disabled={disabled}
                  placeholder={'3rd Floor, Mirembe Business Center\nLugogo Bypass, Kampala, Uganda'}
                />
                <TextField
                  id={`address-${index}-city`}
                  label="City"
                  value={address.city ?? ''}
                  onChange={(value) => updateAddress(index, { city: value })}
                  disabled={disabled}
                />
                <TextField
                  id={`address-${index}-country`}
                  label="Country"
                  value={address.country ?? ''}
                  onChange={(value) => updateAddress(index, { country: value })}
                  disabled={disabled}
                />
                <TextField
                  id={`address-${index}-company`}
                  label="Company"
                  value={address.company_name ?? ''}
                  onChange={(value) => updateAddress(index, { company_name: value })}
                  disabled={disabled}
                />
                <TextField
                  id={`address-${index}-attention`}
                  label="Attention of"
                  value={address.attention_to ?? ''}
                  onChange={(value) => updateAddress(index, { attention_to: value })}
                  disabled={disabled}
                />
                <TextField
                  id={`address-${index}-phone`}
                  label="Phone"
                  type="tel"
                  value={address.phone ?? ''}
                  onChange={(value) => updateAddress(index, { phone: value })}
                  disabled={disabled}
                />
                <TextField
                  id={`address-${index}-email`}
                  label="Email"
                  type="email"
                  inputMode="email"
                  value={address.email ?? ''}
                  onChange={(value) => updateAddress(index, { email: value })}
                  error={errors[`address-${index}-email`]}
                  disabled={disabled}
                />
                <div className="flex flex-wrap items-center gap-4 md:col-span-3">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={address.is_default_billing}
                      onCheckedChange={(checked) =>
                        updateAddress(index, { is_default_billing: checked === true })
                      }
                      disabled={disabled}
                    />
                    Default for billing
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={address.is_default_shipping}
                      onCheckedChange={(checked) =>
                        updateAddress(index, { is_default_shipping: checked === true })
                      }
                      disabled={disabled}
                    />
                    Default for shipping
                  </label>
                </div>
              </div>
            </FieldSet>
          ))}
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAddresses((current) => [...current, blankAddress('OTHER')])}
              disabled={disabled}
            >
              <PlusCircle />
              Add address
            </Button>
          </div>
        </FieldGroup>
      )}

      {wizard.step.id === 'review' && (
        <div className="space-y-6">
          <FieldSet>
            <FieldLegend variant="label">Portal and online</FieldLegend>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="portal_status">Client portal</FieldLabel>
                <EnumSelect
                  value={portalStatus}
                  onValueChange={(value) => setPortalStatus(value as CustomerPortalStatus)}
                  options={options(portalStatuses)}
                  disabled={disabled}
                  triggerProps={{ id: 'portal_status' }}
                />
              </Field>
              <TextField
                id="website_url"
                label="Website"
                value={websiteUrl}
                onChange={setWebsiteUrl}
                error={errors.website_url}
                disabled={disabled}
                placeholder="https://example.org"
                inputMode="url"
              />
              <TextField
                id="social_linkedin"
                label="LinkedIn"
                value={socialLinkedin}
                onChange={setSocialLinkedin}
                disabled={disabled}
                placeholder="https://linkedin.com/company/..."
              />
              <TextField
                id="social_x"
                label="X"
                value={socialX}
                onChange={setSocialX}
                disabled={disabled}
                placeholder="@company"
              />
              <TextField
                id="social_instagram"
                label="Instagram"
                value={socialInstagram}
                onChange={setSocialInstagram}
                disabled={disabled}
                placeholder="@company"
              />
              <TextField
                id="whatsapp_group_id"
                label="WhatsApp group ID"
                value={whatsappGroupId}
                onChange={setWhatsappGroupId}
                disabled={disabled}
              />
              <TextAreaField
                id="notes"
                label="Notes"
                className="sm:col-span-2"
                value={notes}
                onChange={setNotes}
                disabled={disabled}
                placeholder="Operational preferences, tax notes, service expectations."
              />
            </div>
          </FieldSet>

          <div className="space-y-3">
            <ReviewSection title="Profile" onEdit={() => wizard.goToStep('profile')}>
              <ReviewRow label={type === 'BUSINESS' ? 'Company' : 'Name'}>
                {type === 'BUSINESS' ? companyName.trim() : name.trim()}
              </ReviewRow>
              {type === 'BUSINESS' && <ReviewRow label="Main contact">{name.trim()}</ReviewRow>}
              <ReviewRow label="TIN">{tinNumber.trim()}</ReviewRow>
              <ReviewRow label="Email">{email.trim()}</ReviewRow>
              <ReviewRow label="Phone">{phone.trim()}</ReviewRow>
              <ReviewRow label="Customer ID">Assigned by Helm on save</ReviewRow>
              <ReviewRow label="Currency, language">
                {[currency.trim(), language.trim()].filter(Boolean).join(', ')}
              </ReviewRow>
            </ReviewSection>
            <ReviewSection title="Contacts" onEdit={() => wizard.goToStep('contacts')}>
              {namedContacts.length ? (
                namedContacts.map((contact, index) => (
                  <ReviewRow key={index} label={contact.is_primary ? 'Main contact' : 'Contact'}>
                    {[contact.name.trim(), contact.title?.trim(), contact.primary_email?.trim()]
                      .filter(Boolean)
                      .join(' · ')}
                  </ReviewRow>
                ))
              ) : (
                <ReviewRow label="Contacts" />
              )}
            </ReviewSection>
            <ReviewSection title="Addresses" onEdit={() => wizard.goToStep('addresses')}>
              {filledAddresses.length ? (
                filledAddresses.map((address, index) => (
                  <ReviewRow key={index} label={address.label || formatLabel(address.type)}>
                    {address.raw_address?.trim().split('\n')[0] ||
                      [address.recipient_name, address.city, address.country]
                        .map((value) => value?.trim())
                        .filter(Boolean)
                        .join(', ')}
                  </ReviewRow>
                ))
              ) : (
                <ReviewRow label="Addresses" />
              )}
            </ReviewSection>
          </div>
        </div>
      )}
    </FormWizard>
  )
}
